/* global APP, MeetHourJS, config */

import { jitsiLocalStorage } from '@jitsi/js-utils';
import Logger from 'meet-hour-logger';

// import AuthHandler from './modules/UI/authentication/AuthHandler';
import { redirect } from './react/features/base/conference';
import {
    connectionEstablished,
    connectionFailed
} from './react/features/base/connection/actions';
import {
    MHConnectionErrors,
    MHConnectionEvents,
    isFatalMHConnectionError
} from './react/features/base/lib-meet-hour';
import {
    DEFAULT_RUNTIME_WEBSOCKET_FALLBACK_TIMEOUT_MS,
    appendRoomParam,
    readRuntimePreferBoshFlag,
    recordWebSocketFailure
} from './react/features/base/util/connectionUtils';
import { setPrejoinDisplayNameRequired } from './react/features/prejoin/actions';
import { verr, vlog } from './react/features/visitors/debug';

const logger = Logger.getLogger(__filename);

/**
 * The feature announced so we can distinguish jibri participants.
 *
 * @type {string}
 */
export const DISCO_JIBRI_FEATURE = 'http://jitsi.org/protocol/jibri';

/**
 * Checks if we have data to use attach instead of connect. If we have the data
 * executes attach otherwise check if we have to wait for the data. If we have
 * to wait for the attach data we are setting handler to APP.connect.handler
 * which is going to be called when the attach data is received otherwise
 * executes connect.
 *
 * @param {string} [id] - User id.
 * @param {string} [password] - Password.
 * @param {string} [roomName] - The name of the conference.
 */
function checkForAttachParametersAndConnect(id, password, connection) {
    if (window.XMPPAttachInfo) {
        APP.connect.status = 'connecting';

        // When connection optimization is not deployed or enabled the default
        // value will be window.XMPPAttachInfo.status = "error"
        // If the connection optimization is deployed and enabled and there is
        // a failure the value will be window.XMPPAttachInfo.status = "error"
        if (window.XMPPAttachInfo.status === 'error') {
            connection.connect({
                id,
                password
            });

            return;
        }

        const attachOptions = window.XMPPAttachInfo.data;

        if (attachOptions) {
            connection.attach(attachOptions);
            delete window.XMPPAttachInfo.data;
        } else {
            connection.connect({
                id,
                password
            });
        }
    } else {
        APP.connect.status = 'ready';
        APP.connect.handler
            = checkForAttachParametersAndConnect.bind(
                null,
                id, password, connection);
    }
}

/**
 * Try to open connection using provided credentials.
 *
 * @param {string} [id]
 * @param {string} [password]
 * @param {string} [roomName]
 * @returns {Promise<MHConnection>} Connection if
 * everything is ok, else error.
 */
function connect(id, password, roomName) {
    const connectionConfig = { ...config };
    const { jwt } = APP.store.getState()['features/base/jwt'];
    const { preferVisitor } = APP.store.getState()['features/base/connection'];
    const urlParams = new URLSearchParams(window.location.search);
    const forceBosh = urlParams.get('preferBosh') === '1';
    const forceWsInitFailure = urlParams.get('forceWsInitFailure') === '1';
    const runtimePreferBosh = readRuntimePreferBoshFlag(config, logger);
    const preferBosh = Boolean(connectionConfig.preferBosh);
    const hasBosh = Boolean(connectionConfig.bosh);
    const hasWebsocket = Boolean(connectionConfig.websocket);

    // Don't run the websocket->BOSH timeout race for the visitor demote re-join: a slow-but-working socket
    // would be torn down and the BOSH preference would bleed into the vnode connection and recovery probe.
    // A websocket that fails outright (blocked network, untrusted cert) still falls back.
    const shouldTryFallback = hasBosh && hasWebsocket && !forceBosh && !preferBosh && !runtimePreferBosh;
    const shouldRaceFallback = shouldTryFallback && !preferVisitor;


    if (preferVisitor) {
        connectionConfig.preferVisitor = true;
    }

    vlog('5. connection.js connect()', { roomName,
        preferVisitor: Boolean(preferVisitor) });

    return new Promise((resolve, reject) => {
        let activeConnection;
        let isFallbackAttempt = false;
        let websocketFallbackTimer;
        let activeTransport;
        let attemptStartedAt;

        // Set by handleConnectionFailed() when it starts a BOSH fallback, and consumed by
        // connectionFailedHandler() on the very next invocation. See handleConnectionFailed() for why the
        // two cannot simply be ordered by unsubscribe().
        let suppressNextFailureDispatch = false;

        const configuredFallbackTimeout = Number(config?.runtimeWebSocketFallbackTimeoutMs);
        const websocketFallbackTimeoutMs = Number.isFinite(configuredFallbackTimeout) && configuredFallbackTimeout > 0
            ? configuredFallbackTimeout
            : DEFAULT_RUNTIME_WEBSOCKET_FALLBACK_TIMEOUT_MS;

        _startConnectionAttempt(forceBosh || preferBosh || runtimePreferBosh ? 'bosh' : 'websocket');

        /**
         * Clears the pending websocket fallback timer.
         *
         * @returns {void}
         */
        function _clearWebsocketFallbackTimer() {
            clearTimeout(websocketFallbackTimer);
            websocketFallbackTimer = undefined;
        }

        /**
         * Forces immediate retry over BOSH when websocket is blocked or slow to fail.
         *
         * @param {string} reason - Human readable reason for logs.
         * @returns {boolean}
         */
        function _triggerImmediateBoshFallback(reason) {
            if (isFallbackAttempt || !shouldTryFallback) {
                return false;
            }

            _clearWebsocketFallbackTimer();
            recordWebSocketFailure(config, logger);
            logger.warn(`${reason}; retrying with BOSH/http-bind.`);

            const previousConnection = activeConnection;

            unsubscribe();
            isFallbackAttempt = true;

            try {
                previousConnection?.disconnect?.();
            } catch (disconnectError) {
                logger.warn('Failed to disconnect websocket attempt before BOSH fallback.', disconnectError);
            }

            _startConnectionAttempt('bosh');

            return true;
        }

        /**
         * Starts a connection attempt over a specific transport.
         *
         * @param {'websocket'|'bosh'} transport - Preferred transport.
         * @returns {void}
         */
        function _startConnectionAttempt(transport) {
            _clearWebsocketFallbackTimer();

            activeTransport = transport;
            attemptStartedAt = Date.now();

            const attemptConfig = { ...connectionConfig };
            const baseServiceUrl = transport === 'bosh'
                ? connectionConfig.bosh
                : connectionConfig.websocket || connectionConfig.bosh;

            if (!baseServiceUrl) {
                reject(new Error('No XMPP service URL is configured.'));

                return;
            }

            const serviceUrl = appendRoomParam(baseServiceUrl, roomName);

            // FIXME Remove deprecated 'bosh' option assignment at some point(LJM will be accepting only 'serviceUrl'
            //  option in future). It's included for the time being for Meet Hour and lib-meet-hour versions interoperability.
            attemptConfig.serviceUrl = attemptConfig.bosh = serviceUrl;

            if (attemptConfig.websocketKeepAliveUrl) {
                attemptConfig.websocketKeepAliveUrl = appendRoomParam(attemptConfig.websocketKeepAliveUrl, roomName);
            }

            try {
                if (transport === 'websocket' && forceWsInitFailure && !isFallbackAttempt) {
                    throw new Error('Injected websocket initialization failure (forceWsInitFailure=1).');
                }

                activeConnection = new MeetHourJS.MHConnection(attemptConfig.appId, jwt, attemptConfig);

                if (config.iAmRecorder) {
                    activeConnection.addFeature(DISCO_JIBRI_FEATURE);
                }

                activeConnection.addEventListener(
                    MHConnectionEvents.CONNECTION_ESTABLISHED,
                    handleConnectionEstablished);
                activeConnection.addEventListener(
                    MHConnectionEvents.CONNECTION_FAILED,
                    handleConnectionFailed);
                activeConnection.addEventListener(
                    MHConnectionEvents.CONNECTION_FAILED,
                    connectionFailedHandler);
                activeConnection.addEventListener(
                    MHConnectionEvents.CONNECTION_REDIRECTED,
                    _onConnectionRedirected);
                activeConnection.addEventListener(
                    MHConnectionEvents.DISPLAY_NAME_REQUIRED,
                    displayNameRequiredHandler
                );

                if (transport === 'websocket' && shouldRaceFallback && !isFallbackAttempt) {
                    websocketFallbackTimer = setTimeout(() => {
                        _triggerImmediateBoshFallback(
                            `WebSocket attempt timed out after ${websocketFallbackTimeoutMs}ms`
                        );
                    }, websocketFallbackTimeoutMs);
                }

                // Attach optimization is only used on the first try.
                if (isFallbackAttempt) {
                    activeConnection.connect({
                        id,
                        password
                    });

                    return;
                }

                checkForAttachParametersAndConnect(id, password, activeConnection);
            } catch (error) {
                logger.warn(`Connection attempt initialization failed on ${transport}.`, error);

                if (transport === 'websocket'
                    && _triggerImmediateBoshFallback('WebSocket initialization failed')) {
                    return;
                }

                unsubscribe();
                reject(error);
            }
        }

        /**
         *
         */
        function connectionFailedHandler(error, message, credentials, details) {

            // The websocket failure that started a BOSH fallback must not reach redux: the retry is about to
            // succeed, and dispatching a fatal connection error puts the reload/"Reconnecting" overlay in
            // front of a user whose call is fine.
            //
            // This used to be expressed as `!isFallbackAttempt && shouldTryFallback`, which never actually
            // suppressed anything. handleConnectionFailed() runs first and sets isFallbackAttempt to true
            // before this handler is reached, so the guard was always false by the time it was evaluated -
            // and it also swallowed non-transport failures such as PASSWORD_REQUIRED, for which no fallback
            // is started at all, leaving the lobby and authentication flows with nothing to react to.
            if (suppressNextFailureDispatch) {
                suppressNextFailureDispatch = false;

                return;
            }

            const errorPayload = {
                credentials,
                details,
                message,
                name: error
            };

            // During auto-reconnection, mark CONNECTION_DROPPED_ERROR as
            // recoverable so the overlay middleware skips it.
            if (typeof APP !== 'undefined'
                    && APP.conference?.isReconnecting
                    && error === MHConnectionErrors.CONNECTION_DROPPED_ERROR) {
                errorPayload.recoverable = true;
            }

            APP.store.dispatch(
                connectionFailed(
                    activeConnection, errorPayload));

            if (isFatalMHConnectionError(error)) {
                activeConnection.removeEventListener(
                    MHConnectionEvents.CONNECTION_FAILED,
                    connectionFailedHandler);
            }
        }


        /**
             * Connection was redirected.
             *
             * @param {string|undefined} vnode - The vnode to connect to.
             * @param {string} focusJid - The focus jid to use.
             * @param {string|undefined} username - The username to use when joining. This is after promotion from
             * visitor to main participant.
             * @param {string|undefined} role - The main-meeting role granted with the promotion.
             * @private
             * @returns {void}
             */
        function _onConnectionRedirected(vnode, focusJid, username, role) {
            vlog('5b. connection.js CONNECTION_REDIRECTED from jicofo', { vnode,
                focusJid,
                username,
                role });
            activeConnection.removeEventListener(MHConnectionEvents.CONNECTION_REDIRECTED, _onConnectionRedirected);
            APP.store.dispatch(redirect(vnode, focusJid, username, role));
        }

        /**
         * Tears down the connect()-lifecycle listeners.
         *
         * NOTE: This intentionally does NOT remove CONNECTION_REDIRECTED. For a visitor join jicofo
         * emits the redirect AFTER the connection is established (it is the response to the conference
         * request sent during room.join(), which only happens once connect() has resolved). The previous
         * implementation removed CONNECTION_REDIRECTED on establish via this function, silently dropping
         * the visitor redirect so jicofo's vnode was never honoured. The redirect handler removes itself
         * when it fires (see _onConnectionRedirected), mirroring lib-jitsi-meet's connection flow.
         *
         * @returns {void}
         */
        function unsubscribe() {
            if (!activeConnection) {
                return;
            }

            _clearWebsocketFallbackTimer();

            activeConnection.removeEventListener(
                MHConnectionEvents.CONNECTION_ESTABLISHED,
                handleConnectionEstablished);
            activeConnection.removeEventListener(
                MHConnectionEvents.CONNECTION_FAILED,
                handleConnectionFailed);
            activeConnection.removeEventListener(
                MHConnectionEvents.CONNECTION_FAILED,
                connectionFailedHandler);
            activeConnection.removeEventListener(
                MHConnectionEvents.DISPLAY_NAME_REQUIRED,
                displayNameRequiredHandler);
        }

        /**
         *
         */
        function handleConnectionEstablished() {
            vlog('5a. connection.js CONNECTION_ESTABLISHED', { preferVisitor: Boolean(preferVisitor) });

            // Transport telemetry. If production logs show a large share of `transport=bosh`, the websocket
            // fallback is firing on healthy connections rather than broken ones - compare `establishMs` against
            // `runtimeWebSocketFallbackTimeoutMs` before assuming the websocket endpoint is at fault.
            logger.info('[Transport] XMPP connection established'
                + ` transport=${activeTransport}`
                + ` establishMs=${Date.now() - attemptStartedAt}`
                + ` isFallbackAttempt=${isFallbackAttempt}`
                + ` runtimePreferBosh=${runtimePreferBosh}`
                + ` wsFallbackTimeoutMs=${websocketFallbackTimeoutMs}`);

            APP.store.dispatch(connectionEstablished(activeConnection, Date.now()));
            unsubscribe();
            resolve(activeConnection);
        }

        /**
         *
         */
        function handleConnectionFailed(err) {
            // Retry once over BOSH for users whose network blocks websocket - but only for failures that
            // say something about the transport.
            //
            // PASSWORD_REQUIRED is the server answering us over a websocket that demonstrably works. Retrying
            // it over BOSH cannot change the answer, and the retry path calls recordWebSocketFailure(), so
            // every password-protected or lobby-enabled room used to charge the session a websocket failure.
            // Two of those (runtimePreferBoshMinWsFailures) put the tab on BOSH, three locked it there - all
            // without a single websocket ever having failed.
            if (isFatalMHConnectionError(err) && _triggerImmediateBoshFallback('WebSocket connection failed')) {
                // connectionFailedHandler is registered on the same event as this handler, and EventEmitter
                // copies the listener array before dispatching - so it is still invoked for THIS event even
                // though _triggerImmediateBoshFallback() has already detached it via unsubscribe(). Only the
                // in-flight invocation needs suppressing; a later failure belongs to the BOSH attempt and
                // must be reported.
                //
                // The timeout bounds the flag to the current synchronous dispatch instead of trusting that
                // the emitter always copies. If it ever stopped copying, connectionFailedHandler would not
                // run, the flag would stay set, and it would silently swallow the BOSH attempt's own
                // failure - turning a reported error into a mystery. Clearing it on the next macrotask
                // makes "suppress exactly one synchronous invocation" true either way.
                suppressNextFailureDispatch = true;
                setTimeout(() => {
                    suppressNextFailureDispatch = false;
                }, 0);

                return;
            }

            unsubscribe();
            verr('5x. connection.js CONNECTION FAILED', err);
            logger.error('CONNECTION FAILED:', err);
            reject(err);
        }

        /**
         * Marks the display name for the prejoin screen as required.
         * This can happen if a user tries to join a room with lobby enabled.
         */
        function displayNameRequiredHandler() {
            APP.store.dispatch(setPrejoinDisplayNameRequired());
        }

    });
}

export { connect };

/**
 * Open MHConnection using provided credentials.
 * If retry option is true it will show auth dialog on PASSWORD_REQUIRED error.
 *
 * @param {Object} options
 * @param {string} [options.id]
 * @param {string} [options.password]
 * @param {string} [options.roomName]
 * @param {boolean} [retry] - If we should show auth dialog
 * on PASSWORD_REQUIRED error.
 *
 * @returns {Promise<MHConnection>}
 */
export function openConnection({ id, password, retry, roomName }) {
    const usernameOverride
        = jitsiLocalStorage.getItem('xmpp_username_override');
    const passwordOverride
        = jitsiLocalStorage.getItem('xmpp_password_override');

    if (usernameOverride && usernameOverride.length > 0) {
        id = usernameOverride; // eslint-disable-line no-param-reassign
    }
    if (passwordOverride && passwordOverride.length > 0) {
        password = passwordOverride; // eslint-disable-line no-param-reassign
    }

    return connect(id, password, roomName).catch(err => {
        if (retry) {
            const { jwt } = APP.store.getState()['features/base/jwt'];

            if (err === MHConnectionErrors.PASSWORD_REQUIRED && !jwt) {
                // return AuthHandler.requestAuth(roomName, connect);
            }
        }

        throw err;
    });
}
